import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Upload, ShieldCheck, Loader2, FileCheck2, Save, Copy } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";

const PROVINCES = [
  ["ON", "Ontario"], ["AB", "Alberta"], ["BC", "British Columbia"], ["MB", "Manitoba"],
  ["NB", "New Brunswick"], ["NL", "Newfoundland and Labrador"], ["NS", "Nova Scotia"],
  ["PE", "Prince Edward Island"], ["QC", "Quebec"], ["SK", "Saskatchewan"],
  ["NT", "Northwest Territories"], ["NU", "Nunavut"], ["YT", "Yukon"],
];

const UPLOADS = [
  { kind: "sin_id", label: "Copy of SIN + 1 piece of Government ID", required: true },
  { kind: "void_cheque", label: "Void Cheque or Direct Deposit form", required: true },
  { kind: "td1_federal", label: "Signed TD1 Federal", required: true },
  { kind: "td1_ontario", label: "Signed TD1 Ontario", required: true },
];

const EMPTY = {
  first_name: "", last_name: "", preferred_name: "", email: "", phone: "",
  date_of_birth: "", sin: "", address_line1: "", apartment_unit: "", city: "",
  province: "ON", postal_code: "", bank_name: "", bank_institution: "",
  bank_transit: "", bank_account: "", td1_federal_completed: "", td1_provincial_completed: "",
  basic_personal_only: "", additional_tax_per_pay: "", emergency_contact_name: "",
  emergency_contact_relationship: "", emergency_contact_phone: "", consent: false,
  signature: "", date_signed: "",
};

export default function Onboarding() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [company, setCompany] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState({});       // kind -> {file_id, original_filename, uploading}
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [resumeId, setResumeId] = useState(searchParams.get("resume") || null);
  const [saving, setSaving] = useState(false);
  const [resumeUrl, setResumeUrl] = useState("");

  useEffect(() => {
    api.get(`/onboarding/${token}`).then((r) => {
      setCompany(r.data);
      const inv = r.data?.invite;
      if (inv) setForm((f) => ({ ...f, email: f.email || inv.email || "" }));
    }).catch((e) => setLoadError(apiErr(e) || "This onboarding link is invalid."));
  }, [token]);

  useEffect(() => {
    const rid = searchParams.get("resume");
    if (!rid) return;
    api.get(`/onboarding/${token}/draft/${rid}`).then((r) => {
      const d = r.data?.data || {};
      if (d.form) setForm((f) => ({ ...f, ...d.form }));
      if (d.files) setFiles(d.files);
      toast.success("Welcome back — we restored your saved progress");
    }).catch(() => {});
  }, [token, searchParams]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value }));

  const saveDraft = async () => {
    setSaving(true);
    try {
      const cleanFiles = Object.fromEntries(Object.entries(files).filter(([, v]) => v?.file_id));
      const { data } = await api.post(`/onboarding/${token}/draft`, { resume_id: resumeId, form, files: cleanFiles });
      setResumeId(data.resume_id);
      setResumeUrl(data.url);
      await navigator.clipboard.writeText(data.url).catch(() => {});
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const uploadFile = async (kind, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File exceeds the 5MB limit"); return; }
    setFiles((s) => ({ ...s, [kind]: { uploading: true } }));
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      const { data } = await api.post(`/onboarding/${token}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFiles((s) => ({ ...s, [kind]: { file_id: data.file_id, original_filename: data.original_filename } }));
    } catch (e) {
      setFiles((s) => { const n = { ...s }; delete n[kind]; return n; });
      toast.error(apiErr(e));
    }
  };

  const yn = (v) => v === "yes" ? true : v === "no" ? false : null;

  const submit = async () => {
    if (!form.first_name || !form.last_name) { toast.error("Please enter your legal first and last name"); return; }
    if (!form.consent) { toast.error("You must provide consent to submit"); return; }
    for (const u of UPLOADS) {
      if (u.required && !files[u.kind]?.file_id) { toast.error(`Please upload: ${u.label}`); return; }
    }
    setBusy(true);
    try {
      await api.post(`/onboarding/${token}/submit`, {
        ...form,
        resume_id: resumeId || "",
        additional_tax_per_pay: parseFloat(form.additional_tax_per_pay) || 0,
        td1_federal_completed: yn(form.td1_federal_completed),
        td1_provincial_completed: yn(form.td1_provincial_completed),
        basic_personal_only: yn(form.basic_personal_only),
        file_ids: Object.values(files).map((f) => f.file_id).filter(Boolean),
      });
      setDone(true);
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  if (loadError) return <Centered><h1 className="text-2xl font-black">Link unavailable</h1><p className="text-sm text-muted-foreground mt-2 max-w-md" data-testid="ob-load-error">{loadError}</p></Centered>;
  if (!company) return <Centered><Loader2 className="h-6 w-6 animate-spin" /></Centered>;
  if (done) return (
    <Centered>
      <CheckCircle2 className="h-14 w-14 text-emerald-600 mb-4" data-testid="onboarding-success-icon" />
      <h1 className="text-3xl font-black tracking-tight">Thank you!</h1>
      <p className="text-sm text-muted-foreground mt-3 max-w-md">Your onboarding information has been submitted to <b>{company.company_name}</b>'s payroll team at Postings Inc. We'll be in touch if anything else is needed.</p>
    </Centered>
  );

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="border border-black bg-white">
          <div className="bg-black text-white px-6 py-8">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-400">Postings Inc · HR / Payroll Onboarding</div>
            <h1 className="text-3xl font-black tracking-tight mt-2">New Employee Hire Intake — Ontario</h1>
            <p className="text-sm text-neutral-300 mt-3">Congratulations on landing the job! <b className="text-white">{company.company_name}</b> has sent you this link so we can set up your HR &amp; payroll records. Please complete all required fields. Questions? Email <a className="underline" href="mailto:payroll@postings.cloud">payroll@postings.cloud</a>.</p>
          </div>

          <div className="p-6 space-y-8">
            <Section title="Personal Information" desc="Enter your legal and contact details.">
              <G>
                <I label="Legal First Name *" testid="ob-first-name" value={form.first_name} onChange={set("first_name")} />
                <I label="Legal Last Name *" testid="ob-last-name" value={form.last_name} onChange={set("last_name")} />
                <I label="Preferred Name (if different)" testid="ob-preferred" value={form.preferred_name} onChange={set("preferred_name")} />
                <I label="Email Address *" testid="ob-email" type="email" value={form.email} onChange={set("email")} />
                <I label="Phone Number *" testid="ob-phone" value={form.phone} onChange={set("phone")} placeholder="(000) 000-0000" />
                <I label="Date of Birth *" testid="ob-dob" type="date" value={form.date_of_birth} onChange={set("date_of_birth")} />
                <I label="Social Insurance Number (SIN) *" testid="ob-sin" value={form.sin} onChange={set("sin")} placeholder="000 000 000" />
              </G>
            </Section>

            <Section title="Home Address" desc="Provide your current residential address.">
              <G>
                <I label="Street Address *" testid="ob-street" value={form.address_line1} onChange={set("address_line1")} />
                <I label="Apartment / Unit #" testid="ob-unit" value={form.apartment_unit} onChange={set("apartment_unit")} />
                <I label="City *" testid="ob-city" value={form.city} onChange={set("city")} />
                <S label="Province *" testid="ob-province" value={form.province} onChange={set("province")}>
                  {PROVINCES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                </S>
                <I label="Postal Code *" testid="ob-postal" value={form.postal_code} onChange={set("postal_code")} placeholder="A1A 1A1" />
              </G>
            </Section>

            <Section title="Banking Information for Direct Deposit" desc='Required for payroll deposits. If NO direct deposit, enter "0" in each field.'>
              <G>
                <I label="Bank Name *" testid="ob-bank-name" value={form.bank_name} onChange={set("bank_name")} />
                <I label="Institution Number (3 digits) *" testid="ob-institution" value={form.bank_institution} onChange={set("bank_institution")} placeholder="001" />
                <I label="Transit Number (5 digits) *" testid="ob-transit" value={form.bank_transit} onChange={set("bank_transit")} placeholder="12345" />
                <I label="Account Number *" testid="ob-account" value={form.bank_account} onChange={set("bank_account")} />
              </G>
            </Section>

            <Section title="Tax Information — TD1 Forms" desc="Federal and Ontario TD1 forms are required.">
              <G>
                <YN label="Federal TD1 Completed? *" testid="ob-td1-fed" value={form.td1_federal_completed} onChange={set("td1_federal_completed")} />
                <YN label="Ontario TD1 Completed? *" testid="ob-td1-on" value={form.td1_provincial_completed} onChange={set("td1_provincial_completed")} />
                <YN label="Claiming Basic Personal Amount Only? *" testid="ob-basic-only" value={form.basic_personal_only} onChange={set("basic_personal_only")} />
                <I label="Additional Tax to Deduct Per Pay ($, optional)" testid="ob-addl-tax" type="number" value={form.additional_tax_per_pay} onChange={set("additional_tax_per_pay")} />
              </G>
              <div className="mt-3 text-xs text-muted-foreground">
                Download blank forms: <a className="underline" href="https://www.canada.ca/content/dam/cra-arc/formspubs/pbg/td1/td1-fill-26e.pdf" target="_blank" rel="noreferrer">Federal TD1</a> · <a className="underline" href="https://www.canada.ca/content/dam/cra-arc/formspubs/pbg/td1on/td1on-fill-26e.pdf" target="_blank" rel="noreferrer">Ontario TD1</a>
              </div>
            </Section>

            <Section title="Required Documents" desc="Upload the following (PDF, JPEG or PNG · max 5MB each).">
              <div className="space-y-3">
                {UPLOADS.map((u) => (
                  <FileRow key={u.kind} u={u} state={files[u.kind]} onPick={(f) => uploadFile(u.kind, f)} />
                ))}
              </div>
            </Section>

            <Section title="Emergency Contact (optional)" desc="Provide contact details for emergencies.">
              <G>
                <I label="Full Name" testid="ob-ec-name" value={form.emergency_contact_name} onChange={set("emergency_contact_name")} />
                <I label="Relationship to Employee" testid="ob-ec-rel" value={form.emergency_contact_relationship} onChange={set("emergency_contact_relationship")} />
                <I label="Phone Number" testid="ob-ec-phone" value={form.emergency_contact_phone} onChange={set("emergency_contact_phone")} placeholder="(000) 000-0000" />
              </G>
            </Section>

            <Section title="Declaration & Consent" desc="Please review and provide your consent.">
              <label className="flex items-start gap-3 text-sm cursor-pointer">
                <input type="checkbox" data-testid="ob-consent" checked={form.consent} onChange={set("consent")} className="mt-1 accent-black h-4 w-4" />
                <span>I declare that the information provided is true and complete. I consent to the collection and use of my information for employment and payroll purposes.</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <I label="Employee Signature (type your full legal name) *" testid="ob-signature" value={form.signature} onChange={set("signature")} />
                <I label="Date Signed *" testid="ob-date-signed" type="date" value={form.date_signed} onChange={set("date_signed")} />
              </div>
            </Section>

            <div className="flex items-center gap-2 text-xs text-muted-foreground border-t border-border pt-4">
              <ShieldCheck className="h-4 w-4 text-emerald-600" /> Your information is transmitted securely and only shared with your employer's payroll administrators.
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button data-testid="ob-save-later" disabled={saving} onClick={saveDraft} className="sm:w-56 border border-black bg-white py-4 text-sm font-medium hover:bg-black hover:text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save &amp; continue later
              </button>
              <button data-testid="ob-submit" disabled={busy} onClick={submit} className="flex-1 bg-black text-white py-4 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : "Submit onboarding form"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!resumeUrl} onOpenChange={(o) => !o && setResumeUrl("")}>
        <DialogContent className="rounded-none border-black w-[95vw] sm:max-w-xl max-h-[94vh] overflow-y-auto" data-testid="ob-resume-dialog">
          <DialogHeader>
            <DialogTitle className="tracking-tight">Progress saved</DialogTitle>
            <DialogDescription className="text-xs">Bookmark or save this private link to finish your form later — from any device. We've copied it to your clipboard.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 py-2">
            <input readOnly data-testid="ob-resume-url" value={resumeUrl} className="flex-1 border border-input bg-neutral-50 px-3 py-2.5 text-xs font-mono focus:outline-none" />
            <button data-testid="ob-resume-copy" onClick={() => { navigator.clipboard.writeText(resumeUrl); toast.success("Link copied"); }} className="border border-black px-3 py-2.5 hover:bg-black hover:text-white transition-colors">
              <Copy className="h-4 w-4" />
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center text-center px-6">{children}</div>;
}
function Section({ title, desc, children }) {
  return (
    <div className="border-t border-border pt-6 first:border-0 first:pt-0">
      <h2 className="text-lg font-bold tracking-tight">{title}</h2>
      {desc && <p className="text-xs text-muted-foreground mt-1 mb-4">{desc}</p>}
      {children}
    </div>
  );
}
function G({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>; }
function I({ label, value, onChange, testid, type = "text", placeholder }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</label>
      <input data-testid={testid} type={type} value={value} placeholder={placeholder} onChange={onChange} className="mt-1.5 w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
    </div>
  );
}
function S({ label, value, onChange, testid, children }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</label>
      <select data-testid={testid} value={value} onChange={onChange} className="mt-1.5 w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black">{children}</select>
    </div>
  );
}
function YN({ label, value, onChange, testid }) {
  return (
    <S label={label} value={value} onChange={onChange} testid={testid}>
      <option value="">Please select</option>
      <option value="yes">Yes</option>
      <option value="no">No</option>
    </S>
  );
}
function FileRow({ u, state, onPick }) {
  const done = !!state?.file_id;
  return (
    <div className="flex items-center gap-3 border border-border bg-white px-4 py-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{u.label} {u.required && <span className="text-red-600">*</span>}</div>
        {done && <div className="text-xs text-emerald-600 flex items-center gap-1 mt-0.5"><FileCheck2 className="h-3.5 w-3.5" /> {state.original_filename}</div>}
      </div>
      <label data-testid={`ob-upload-${u.kind}`} className="cursor-pointer inline-flex items-center gap-2 border border-black px-3 py-2 text-xs font-medium hover:bg-black hover:text-white transition-colors whitespace-nowrap">
        {state?.uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
        {done ? "Replace" : "Choose file"}
        <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => onPick(e.target.files?.[0])} />
      </label>
    </div>
  );
}
